import React, { useEffect, useRef, useState } from 'react';
import { Card, Input, Button, InsetText, Textarea, ErrorSummary, Select, Checkboxes, Radios, Fieldset } from 'nhsuk-react-components';
import Callout from '../../callout/Callout.component';
import SelectOther from '../../selectOther/SelectOther.component';
import ISODateInput from '../../date/ISODateInput.component';
import InputBarcodeScanner from './InputBarcodeScanner';
import ImplantEntitySelect from '../../implantEntitySelect/ImplantEntitySelect.component';

const CreateImplantModal = ({
    newImplantInstance,
    setNewImplantInstance,
    handleCreateImplantInstance,
    implantInstanceUpdateProcessing,
    successCreate,
    successUpdate,
    implantInstanceCreateErrors,
    implantInstanceUpdateErrors,
    loadingImplantDetailsByBarcode,
    handleGetImplantByBarcode,
    scannedBarcode,
    setScannedBarcode,
    modelNotFound,
    autoFillDetail,
    isGenerator,
    isLead,
    onClose,
    onScanAgain
}) => {
    const pendingAction = useRef(null);
    const [searchMethod, setSearchMethod] = useState('barcode'); // 'barcode' or 'search'
    const [selectedImplant, setSelectedImplant] = useState(null);

    // Handle success actions based on which button was clicked
    useEffect(() => {
        if (successCreate && pendingAction.current) {
            if (pendingAction.current === 'close' && typeof onClose === 'function') {
                onClose();
            } else if (pendingAction.current === 'scanAgain' && typeof onScanAgain === 'function') {
                onScanAgain();
            }
            pendingAction.current = null; // Reset after handling
        }
    }, [successCreate, onClose, onScanAgain]);
    return (
        <Card id='create-card'>
            <Card.Content>
                <Card.Heading className="nhsuk-heading-m">
                    Create a new Implant
                </Card.Heading>
                <Card.Description>
                    {implantInstanceCreateErrors && (
                        <ErrorSummary>
                            <ErrorSummary.Title id="error-summary-title">
                                There is a problem
                            </ErrorSummary.Title>
                            <ErrorSummary.Body>
                                <ul>
                                    {implantInstanceCreateErrors.map((error, index) => (
                                        <li key={index}>
                                            {error}
                                        </li>
                                    ))}
                                </ul>
                            </ErrorSummary.Body>
                        </ErrorSummary>
                    )}

                    <ISODateInput
                        id="eventDate"
                        label="Implantation Date"
                        name="eventDate"
                        value={newImplantInstance?.eventDate}
                        onChange={(dt) => setNewImplantInstance({ ...newImplantInstance, eventDate: dt })}
                        required
                    />

                    <Fieldset>
                        <Fieldset.Legend>Search Method</Fieldset.Legend>
                        <Radios name="searchMethod" id="search-method">
                            <Radios.Radio
                                value="barcode"
                                checked={searchMethod === 'barcode'}
                                onChange={(e) => setSearchMethod(e.target.value)}
                            >
                                Barcode Scanner
                            </Radios.Radio>
                            <Radios.Radio
                                value="search"
                                checked={searchMethod === 'search'}
                                onChange={(e) => setSearchMethod(e.target.value)}
                            >
                                Search Implant Database
                            </Radios.Radio>
                        </Radios>
                    </Fieldset>

                    {searchMethod === 'barcode' ? (
                        <InsetText>
                            <InputBarcodeScanner
                                value={scannedBarcode}
                                onChange={(value) => {
                                    const barcodeValue = typeof value === 'object' ? value.target.value : value;
                                    setScannedBarcode(barcodeValue);
                                }}
                                onSearch={(value) => {
                                    if (autoFillDetail) {
                                        handleGetImplantByBarcode(value);
                                    }
                                }}
                                label="Barcode/GTIN"
                                name="barcode"
                                type="text"
                                required
                            />
                            {autoFillDetail && (
                                <Button
                                    disabled={loadingImplantDetailsByBarcode}
                                    onClick={() => handleGetImplantByBarcode(scannedBarcode)}
                                >
                                    {loadingImplantDetailsByBarcode ? 'Loading...' : 'Auto-check details'}
                                </Button>
                            )}
                            {modelNotFound && (
                                <p style={{ color: 'red', marginTop: '10px' }}>Model not found in the database. Please enter details manually or Register a new model.</p>
                            )}
                        </InsetText>
                    ) : (
                        <InsetText>
                            <ImplantEntitySelect
                                selectedId={selectedImplant?.id || null}
                                onChange={(implant) => {
                                    if (implant) {
                                        setSelectedImplant(implant);
                                        setNewImplantInstance({
                                            ...newImplantInstance,
                                            barcode: implant.barcode || '',
                                            manufacturer: implant.manufacturer || '',
                                            modelName: implant.modelName || '',
                                            modelNumber: implant.modelNumber || '',
                                            modelCategory: implant.modelCategory || '',
                                            mrConditional: implant.mrConditional || false,
                                        });
                                    } else {
                                        setSelectedImplant(null);
                                    }
                                }}
                                title="Select Implant from Database"
                                enableCreate={true}
                            />
                        </InsetText>
                    )}

                    <Input
                        id="manufacturer"
                        label="Manufacturer"
                        name="manufacturer"
                        type="text"
                        onChange={(e) => setNewImplantInstance({ ...newImplantInstance, manufacturer: e.target.value })}
                        value={newImplantInstance?.manufacturer}
                        required
                    />

                    <Input
                        id="modelName"
                        label="Model Name"
                        name="modelName"
                        type="text"
                        onChange={(e) => setNewImplantInstance({ ...newImplantInstance, modelName: e.target.value })}
                        value={newImplantInstance?.modelName}
                        required
                    />

                    <Input
                        id="modelNumber"
                        label="Model Number"
                        name="modelNumber"
                        type="text"
                        onChange={(e) => setNewImplantInstance({ ...newImplantInstance, modelNumber: e.target.value })}
                        value={newImplantInstance?.modelNumber}
                        required
                    />

                    <Input
                        id="serialNumber"
                        label="Serial Number"
                        name="serialNumber"
                        type="text"
                        onChange={(e) => setNewImplantInstance({ ...newImplantInstance, serialNumber: e.target.value })}
                        value={newImplantInstance?.serialNumber}
                        required
                    />

                    <InsetText>
                        <Select
                            id="modelCategory"
                            label="Model Category"
                            name="modelCategory"
                            onChange={(e) => setNewImplantInstance({ ...newImplantInstance, modelCategory: e.target.value })}
                            value={newImplantInstance?.modelCategory}
                            required
                        >
                            <Select.Option value="">Select a model category</Select.Option>
                            <Select.Option value="Accessory">Accessory</Select.Option>
                            <Select.Option value="Generator">Generator</Select.Option>
                            <Select.Option value="Lead">Lead</Select.Option>
                            <Select.Option value="Implantable Monitor">Implantable Monitor</Select.Option>
                        </Select>

                        {isGenerator && (
                            <div>
                                <SelectOther
                                    id="generatorImplantSite"
                                    label="Generator Implant Site"
                                    name="generatorImplantSite"
                                    onChange={(e) => setNewImplantInstance({ ...newImplantInstance, generatorImplantSite: e.target.value })}
                                    value={newImplantInstance?.generatorImplantSite}
                                >
                                    <Select.Option value="">Select a generator implant site</Select.Option>
                                    <Select.Option value="Chest wall - subcutaneous">Chest wall - subcutaneous</Select.Option>
                                    <Select.Option value="Chest wall - submuscular">Chest wall - submuscular</Select.Option>
                                    <Select.Option value="Abdominal - subcutaneous">Abdominal - subcutaneous</Select.Option>
                                    <Select.Option value="Abdominal - submuscular">Abdominal - submuscular</Select.Option>
                                    <Select.Option value="Intravascular - endocardial">Intravascular - endocardial</Select.Option>
                                    <Select.Option value="Intravascular - pulmonary artery">Intravascular - pulmonary artery</Select.Option>
                                </SelectOther>

                                <Select
                                    id="maxSystemCapability"
                                    label="Max System Capability"
                                    name="maxSystemCapability"
                                    onChange={(e) => setNewImplantInstance({ ...newImplantInstance, maxSystemCapability: e.target.value })}
                                    value={newImplantInstance?.maxSystemCapability}
                                >
                                    <Select.Option value="">Select a max system capability</Select.Option>
                                    <Select.Option value="PM-AAI(R)">PM-AAI(R)</Select.Option>
                                    <Select.Option value="CRTD">CRTD</Select.Option>
                                    <Select.Option value="CRTP">CRTP</Select.Option>
                                    <Select.Option value="PM-DDD(R)">PM-DDD(R)</Select.Option>
                                    <Select.Option value="ICD-DR">ICD-DR</Select.Option>
                                    <Select.Option value="ICD-SQ">ICD-SQ</Select.Option>
                                    <Select.Option value="ICD-VR">ICD-VR</Select.Option>
                                    <Select.Option value="Monitor">Monitor</Select.Option>
                                    <Select.Option value="None">None</Select.Option>
                                    <Select.Option value="Other">Other</Select.Option>
                                    <Select.Option value="PM-VVI(R)">PM-VVI(R)</Select.Option>
                                    <Select.Option value="PM-VDD(R) - single lead">PM-VDD(R) - single lead</Select.Option>
                                    <Select.Option value="ICD -VDD(R) - single lead">ICD -VDD(R) - single lead</Select.Option>
                                </Select>
                            </div>
                        )}

                        {isLead && (
                            <div>
                                <Select
                                    id="leadChamber"
                                    label="Lead Chamber"
                                    name="leadChamber"
                                    onChange={(e) => setNewImplantInstance({ ...newImplantInstance, leadChamber: e.target.value })}
                                    value={newImplantInstance?.leadChamber}
                                >
                                    <Select.Option value="">Select a lead chamber</Select.Option>
                                    <Select.Option value="Right Atrium">Right Atrium</Select.Option>
                                    <Select.Option value="Right Ventricle">Right Ventricle</Select.Option>
                                    <Select.Option value="Left Ventricle">Left Ventricle</Select.Option>
                                    <Select.Option value="NA">NA</Select.Option>
                                </Select>

                                <SelectOther
                                    id="pacingSite"
                                    label="Pacing Site"
                                    name="pacingSite"
                                    onChange={(e) => setNewImplantInstance({ ...newImplantInstance, pacingSite: e.target.value })}
                                    value={newImplantInstance?.pacingSite}
                                >
                                    <Select.Option value="">Select a pacing site</Select.Option>
                                    <Select.Option value="Atrial other">Atrial other</Select.Option>
                                    <Select.Option value="CS - Anterior branch">CS - Anterior branch</Select.Option>
                                    <Select.Option value="CS - Anterolateral branch">CS - Anterolateral branch</Select.Option>
                                    <Select.Option value="CS - Lateral branch">CS - Lateral branch</Select.Option>
                                    <Select.Option value="CS - Posterior branch">CS - Posterior branch</Select.Option>
                                    <Select.Option value="CS - Posterolateral branch">CS - Posterolateral branch</Select.Option>
                                    <Select.Option value="Epicardial (surgical)">Epicardial (surgical)</Select.Option>
                                    <Select.Option value="Greater cardiac vein">Greater cardiac vein</Select.Option>
                                    <Select.Option value="His/Conduction system">His/Conduction system</Select.Option>
                                    <Select.Option value="LA via CS">LA via CS</Select.Option>
                                    <Select.Option value="LV endocardial - trans-septal">LV endocardial - trans-septal</Select.Option>
                                    <Select.Option value="LV epicardial - via pericardium">LV epicardial - via pericardium</Select.Option>
                                    <Select.Option value="LV other">LV other</Select.Option>
                                    <Select.Option value="Middle cardiac vein">Middle cardiac vein</Select.Option>
                                    <Select.Option value="RA endocardial">RA endocardial</Select.Option>
                                    <Select.Option value="RV apex endocardial">RV apex endocardial</Select.Option>
                                    <Select.Option value="RV epicardial (surgical)">RV epicardial (surgical)</Select.Option>
                                    <Select.Option value="RV other">RV other</Select.Option>
                                    <Select.Option value="RV outflow endocardial">RV outflow endocardial</Select.Option>
                                    <Select.Option value="RV septal">RV septal</Select.Option>
                                </SelectOther>
                            </div>
                        )}
                    </InsetText>

                    <Select
                        id="status"
                        label="Status"
                        name="status"
                        onChange={(e) => setNewImplantInstance({ ...newImplantInstance, status: e.target.value })}
                        value={newImplantInstance?.status}
                        required
                    >
                        <Select.Option value="">Select a status</Select.Option>
                        <Select.Option value="Implanted, connected">Implanted, connected</Select.Option>
                        <Select.Option value="Explanted">Explanted</Select.Option>
                        <Select.Option value="Partial extraction">Partial extraction</Select.Option>
                        <Select.Option value="Complete extraction">Complete extraction</Select.Option>
                        <Select.Option value="Implanted, abandoned">Implanted, abandoned</Select.Option>
                    </Select>


                    <ISODateInput
                        id="expiryDate"
                        label="Expiry Date"
                        name="expiryDate"
                        value={newImplantInstance?.expiryDate}
                        onChange={(dt) => setNewImplantInstance({ ...newImplantInstance, expiryDate: dt })}
                        required
                    />

                    <Checkboxes name="checkboxes">
                        <Checkboxes.Box
                            checked={newImplantInstance?.abandonedHardware}
                            onChange={(e) => setNewImplantInstance({ ...newImplantInstance, abandonedHardware: e.target.checked })}
                        >
                            Abandoned Hardware
                        </Checkboxes.Box>
                    </Checkboxes>

                    <Textarea
                        id="eventDetails"
                        label="Event Details"
                        name="eventDetails"
                        onChange={(e) => setNewImplantInstance({ ...newImplantInstance, eventDetails: e.target.value })}
                        value={newImplantInstance?.eventDetails}
                    />

                    <Textarea
                        id="PractitionerDetails"
                        label="Practitioner Details"
                        name="PractitionerDetails"
                        onChange={(e) => setNewImplantInstance({ ...newImplantInstance, practitionerDetails: e.target.value })}
                        value={newImplantInstance?.practitionerDetails}
                    />

                    <Textarea
                        id="Notes"
                        label="Notes"
                        name="Notes"
                        onChange={(e) => setNewImplantInstance({ ...newImplantInstance, notes: e.target.value })}
                        value={newImplantInstance?.notes}
                    />

                    {(successCreate || successUpdate) && (
                        <Callout
                            label="Success"
                            header="Success!"
                            autoDismiss={true}
                            dismissTime={5000}
                            time={new Date().toLocaleTimeString()}
                            title="Success!"
                            message={successCreate ? "The implantInstance was created successfully." : "The implantInstance was updated successfully."}
                            mode="success"
                        />
                    )}

                    {(implantInstanceCreateErrors || implantInstanceUpdateErrors) && (
                        <Callout
                            label="Error"
                            header="Error!"
                            autoDismiss={true}
                            dismissTime={5000}
                            time={new Date().toLocaleTimeString()}
                            title="Error!"
                            message={"There was an error creating the implantInstance. Please scroll up to see the error message."}
                            mode="error"
                        />
                    )}
                </Card.Description>

                <div style={{ display: 'flex', gap: '1rem' }}>
                    <Button
                        disabled={implantInstanceUpdateProcessing}
                        onClick={() => {
                            pendingAction.current = 'close';
                            handleCreateImplantInstance();
                        }}
                    >
                        Save and Close
                    </Button>
                    <Button
                        disabled={implantInstanceUpdateProcessing}
                        onClick={() => {
                            pendingAction.current = 'scanAgain';
                            handleCreateImplantInstance();
                        }}
                        secondary
                    >
                        Save and Scan again
                    </Button>
                </div>
            </Card.Content>
        </Card>
    );
};

export default CreateImplantModal; 